import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../app/hooks'
import { fetchSessionsPaginated } from '../features/sessions/slice-extensions/sessions-slice-extension'
import { createPaginationRequest } from '../features/sessions/contracts/PaginationRequest'
import { LoadingStatus } from '../features/sessions/models/LoadingStatus'
import { Session } from '../features/sessions/models/Session'
import LeftMenu from './LeftMenu'
import Breadcrumbs from './Breadcrumbs'
import SessionCard from './SessionCard'
import Tabs from './Tabs'

const SessionsList: React.FC = () => {
  const dispatch = useAppDispatch()
  const { entities: sessions, status, pagination } = useAppSelector(state => state.sessions)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedTab, setSelectedTab] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)

  // Initialize selectedTab based on URL query parameter
  useEffect(() => {
    const view = searchParams.get('view')
    if (view === 'upcoming') {
      setSelectedTab(0)
    } else if (view === 'completed') {
      setSelectedTab(1)
    }
  }, [searchParams])

  // Fetch sessions on component mount and when page changes
  useEffect(() => {
    const paginationRequest = createPaginationRequest(currentPage, 10)
    dispatch(fetchSessionsPaginated(paginationRequest))
  }, [dispatch, currentPage])

  // Group sessions by date for display
  const groupSessionsByDate = (sessions: Session[]): { dateLabel: string; sessions: Session[] }[] => {
    const grouped = new Map<string, Session[]>();

    sessions.forEach(session => {
      const dateKey = formatDateLabel(new Date(session.dateStart));
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(session);
    });

    return Array.from(grouped.entries()).map(([dateLabel, sessionGroup]) => ({
      dateLabel,
      sessions: sessionGroup,
    }));
  };

  const formatDateLabel = (date: Date): string => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const dayNumber = date.getDate();
    
    return `${dayName}, ${monthName} ${dayNumber}`;
  };

  const groupedSessions = groupSessionsByDate(sessions)

  // Handle tab change
  const handleTabChange = (tabIndex: number) => {
    setSelectedTab(tabIndex)
    // Update URL query parameter
    const newSearchParams = new URLSearchParams(searchParams)
    if (tabIndex === 0) {
      newSearchParams.set('view', 'upcoming')
    } else if (tabIndex === 1) {
      newSearchParams.set('view', 'completed')
    }
    setSearchParams(newSearchParams)
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Loading state
  if (status === LoadingStatus.Loading) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <p className="text-lg">Loading sessions...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side */}
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[694px]">
            {/* [START] HTML Breadcrumbs */}
            <Breadcrumbs />
            {/* [END] HTML Breadcrumbs */}
            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h1>Schedule</h1>
                <button className="btn btn-secondary">
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 2C17.5523 2 18 2.44772 18 3V5H19C20.6569 5 22 6.34315 22 8V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V8C2 6.34315 3.34315 5 5 5H6V3C6 2.44772 6.44772 2 7 2C7.55228 2 8 2.44772 8 3V5H16V3C16 2.44772 16.4477 2 17 2ZM4 11V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V11H4ZM5 7C4.44772 7 4 7.44772 4 8V9H20V8C20 7.44772 19.5523 7 19 7H5Z"
                      fill="#4D4D4D"
                    />
                  </svg>
                  <span>View Calendar</span>
                </button>
              </div>
              <div className="space-y-6 lg:space-y-4">
                {/* Start Time */}
                <div className="space-y-2">
                  <p className="font-bold">Start Time</p>
                  <div>
                    <Tabs
                      tabs={["Upcoming Sessions", "Completed Sessions"]}
                      selectedIndex={selectedTab}
                      onSelect={handleTabChange}
                      headerClassName="grid-cols-2"
                      idPrefix="tab"
                    />
                    <div className="tabs-content mt-5">
                      <div
                        id="panel-0"
                        className={`tab-content animate-fadeIn ${selectedTab === 0 ? '' : 'hidden'}`.trim()}
                        role="tabpanel"
                        aria-labelledby="tab-0"
                        aria-hidden={selectedTab === 0 ? undefined : true}
                      >
                        <div className="flex max-w-[698px] flex-wrap gap-4">
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="upcoming"
                              defaultValue={1}
                              className="peer hidden"
                              defaultChecked
                            />
                            <span className="checkbox-group-item py-0">
                              All
                            </span>
                          </label>
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="upcoming"
                              defaultValue={1}
                              className="peer hidden"
                            />
                            <span className="checkbox-group-item py-0">
                              Today
                            </span>
                          </label>
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="upcoming"
                              defaultValue={1}
                              className="peer hidden"
                            />
                            <span className="checkbox-group-item py-0">
                              This Week
                            </span>
                          </label>
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="upcoming"
                              defaultValue={1}
                              className="peer hidden"
                            />
                            <span className="checkbox-group-item py-0">
                              This Month
                            </span>
                          </label>
                        </div>
                      </div>
                      <div
                        id="panel-1"
                        className={`tab-content animate-fadeIn ${selectedTab === 1 ? '' : 'hidden'}`.trim()}
                        role="tabpanel"
                        aria-labelledby="tab-1"
                        aria-hidden={selectedTab === 1 ? undefined : true}
                      >
                        <div className="flex max-w-[698px] flex-wrap gap-4">
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="completed"
                              defaultValue={1}
                              className="peer hidden"
                              defaultChecked
                            />
                            <span className="checkbox-group-item py-0">
                              All
                            </span>
                          </label>
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="completed"
                              defaultValue={1}
                              className="peer hidden"
                            />
                            <span className="checkbox-group-item py-0">
                              Today
                            </span>
                          </label>
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="completed"
                              defaultValue={1}
                              className="peer hidden"
                            />
                            <span className="checkbox-group-item py-0">
                              This Week
                            </span>
                          </label>
                          <label className="flex cursor-pointer">
                            <input
                              type="radio"
                              name="completed"
                              defaultValue={1}
                              className="peer hidden"
                            />
                            <span className="checkbox-group-item py-0">
                              This Month
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Sessions Display */}
              <div className="space-y-6 lg:space-y-4">
                {status === LoadingStatus.Failed && (
                  <div className="rounded-lg bg-red-50 p-4 text-red-700">
                    <p>Error loading sessions. Please try again.</p>
                  </div>
                )}
                
                {groupedSessions.length === 0 && status === LoadingStatus.Idle && (
                  <div className="rounded-lg bg-gray-50 p-8 text-center">
                    <p className="text-gray-600">No sessions found.</p>
                  </div>
                )}
                
                {groupedSessions.map((day, di) => (
                  <div key={di} className="space-y-2 lg:space-y-4">
                    <h3>{day.dateLabel}</h3>
                    <div className="space-y-2 lg:space-y-4">
                      {day.sessions.map((session, si) => (
                        <SessionCard key={session.id || `${di}-${si}`} session={session} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* [START] HTML Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing page {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total sessions)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className="btn btn-secondary disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="btn btn-secondary disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* [END] HTML Pagination */}
            
            {/* Mobile Create Event Button */}
            <div className="dark:bg-primary-20 fixed bottom-0 left-0 z-10 flex w-full items-center justify-center bg-white py-4 after:absolute after:-top-8 after:h-8 after:w-full after:bg-linear-(--gradient-white) lg:hidden dark:after:hidden">
              <button className="btn btn-primary">
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    className="fill-current"
                    d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z"
                  />
                </svg>
                <span>New Session</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default SessionsList
